import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticateVleUserSilentlyConfig } from '@technologyfromsage/persona-padlock';
import { NEVER, Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthenticationFailedException } from '®shared/auth/authentication-failed-exception';
import { AppConfig } from '®shared/config';
import { RedirectionService } from '®shared/redirection';

import { AuthProvider } from './auth-provider';
import { AuthServiceLogin } from './auth-service-login';
import { PersonaPadlockService } from './persona-padlock.service';
import { PersonaPkceSharedService } from './persona-pkce-shared.service';
import { PersonaUser, createPersonaUserFromPkceLogin } from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

@Injectable()
export class PersonaPkceLtiAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  constructor(
    private personaPadlockService: PersonaPadlockService,
    private personaPkceSharedService: PersonaPkceSharedService,
    private router: Router,
    appConfig: AppConfig,
    redirectionService: RedirectionService,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, redirectionService, userScopesService);
  }

  private targetUri: URI | null = null;

  public get postBreakoutLoginRedirectUri(): string {
    return this.targetUri.toString();
  }

  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();

    const ltiAuthenticationRoute = '3/lti-authentication.html';

    this.targetUri = new URI(currentUri.resource());
    return from(this.router.navigateByUrl(ltiAuthenticationRoute)).pipe(
      switchMap(() => NEVER),
      catchError(
        this.personaPkceSharedService.throwAuthenticationFailedException,
      ),
    );
  }

  public getCurrentToken(): Observable<User> {
    const currentUri = this.getCurrentUri();
    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(currentUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);
    const ltiAuthNonce = currentUri.search(true).lti_auth_nonce;

    const ltiConfig: AuthenticateVleUserSilentlyConfig = {
      personaConfig: config,
      ltiAuthNonce,
      ltiAuthorizeUrl: `${this.appConfig.apiEndpoint}/lti/authorize`,
    };

    return this.personaPkceSharedService.getCurrentToken(() =>
      this.personaPadlockService.authenticateVleUserSilently(ltiConfig),
    );
  }

  public refreshToken(): Observable<User> {
    return this.personaPkceSharedService.refreshToken(this.currentUser);
  }

  public login(): void {
    const returnUrl = this.getCurrentUri();
    returnUrl.removeQuery('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase);
    returnUrl.searchParams.delete('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public performLtiBreakoutLogin(): Observable<PersonaUser> {
    if (!this.targetUri) {
      return throwError(
        new AuthenticationFailedException(
          'Unable to authenticate: No target URI set, call obtainNewToken first',
        ),
      );
    }

    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(this.targetUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    return this.personaPadlockService
      .authenticateVleUserViaBreakout(config)
      .pipe(
        map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
      );
  }
}
